import {useEffect, useState} from "react"
import {createPortal} from "react-dom"
import IconMore from "../../assets/images/icon.more.svg"

/** 메뉴 폭. 아이콘 왼쪽으로 펼친다(오른쪽 끝 행에서 화면을 넘지 않도록). */
const MENU_WIDTH = 140

/**
 * 행 우측의 `⋯` 확장 메뉴.
 *
 * 🔴 **`document.body` 로 포털**해서 그린다(2026-08-27, 2차 수정).
 *
 * 1차로 `position: fixed` 로만 바꿨더니 증상이 남았다 — "클릭해도 안 뜨고 마우스를 빼면 뜬다",
 * "메뉴에 마우스를 올리면 빠르게 깜박인다".
 *
 * 원인 = **`fixed` 는 transform 된 조상이 있으면 뷰포트가 아니라 그 조상을 기준으로 배치되고,
 * 조상의 stacking context 에도 그대로 갇힌다.** `GenericBoard` 의 행이 `hover:-translate-y-0.5`
 * 를 갖고, 메뉴를 열려면 그 행을 hover 해야 하므로 **열자마자는 항상 갇힌 상태**다.
 * 마우스를 빼면 transform 이 풀려 그때 뷰포트 기준으로 튀어나온다 → "빼면 뜬다".
 * 깜박임도 같은 원인의 진동이다(메뉴로 이동 → 행 hover 해제 → 메뉴가 튀어나가 커서에서 벗어남
 * → 행 재hover → 다시 갇힘 → 반복).
 *
 * ⇒ **DOM 서브트리 자체를 벗어나야 한다.** 포털이면 조상의 transform·stacking context·overflow
 *   전부와 무관해진다. 좌표는 **클릭 시점에 동기로** 계산해 2단 렌더(빈 프레임)도 없앤다.
 */
const MiniMenu = ({id, openMenuId, setOpenMenuId, onClickEdit, disabledEdit, onClickPreview, deletedName, onClickDelete, disabledDelete}) => {
    const isOpen = openMenuId === id;
    const [pos, setPos] = useState(null)

    // 스크롤·리사이즈로 아이콘이 움직이면 포털 메뉴만 제자리에 남아 어긋난다 → 닫는다.
    useEffect(() => {
        if (!isOpen) return
        const close = () => setOpenMenuId(null)
        window.addEventListener("scroll", close, true)
        window.addEventListener("resize", close)
        return () => {
            window.removeEventListener("scroll", close, true)
            window.removeEventListener("resize", close)
        }
    }, [isOpen, setOpenMenuId])

    useEffect(() => {
        if (!isOpen) setPos(null)
    }, [isOpen])

    const toggle = (e) => {
        // 좌표를 클릭 이벤트에서 바로 잡는다(useLayoutEffect 로 미루면 한 프레임 비어 보인다).
        const r = e.currentTarget.getBoundingClientRect()
        setPos({ top: r.bottom + 4, left: r.right - MENU_WIDTH })
        setOpenMenuId(prevId => (prevId === id ? null : id))
    }

    const menu = (
        <ul className="fixed bg-white rounded-lg text-sm shadow-lg border border-gray-200"
            style={{width: `${MENU_WIDTH}px`, top: `${pos?.top}px`, left: `${pos?.left}px`, zIndex: 1000}}
        >
            <li
                className={`py-3 px-6 rounded-t-lg ${disabledEdit ? 'none' : 'hover:bg-gray-50 cursor-pointer'}`}
                onClick={(e) => {
                    if (!disabledEdit) {
                        onClickEdit(e)
                    }
                }}
            >
                수정하기
            </li>
            {
                onClickPreview && (
                    <li className="hover:bg-gray-50 cursor-pointer py-3 px-6" onClick={onClickPreview}>
                        미리보기
                    </li>
                )
            }
            {
                onClickDelete && (
                    <li
                        className={`hover:bg-gray-50 py-3 px-6 rounded-b-lg text-red-500 ${disabledDelete ? 'none' : 'cursor-pointer'}`}
                        onClick={onClickDelete}
                    >
                        {deletedName ?? "삭제하기"}
                    </li>
                )
            }
        </ul>
    )

    return (
        <div>
            <img alt="" className="pointer" src={IconMore} onClick={toggle} />
            {isOpen && pos && createPortal(menu, document.body)}
        </div>
    )
}

export default MiniMenu
